import { Archive } from "lucide-react-native";
import { FC, useState } from "react";

import { useLocalization } from "@/hooks";

import { Button, ButtonText, HStack, Text } from "./base";
import DeleteDialog from "./DeleteDialog";

interface ArchivedBannerProps {
  canRestore: boolean;
  onRestore: () => Promise<boolean | undefined>;
  resourceType: "study" | "question";
}

const ArchivedBanner: FC<ArchivedBannerProps> = ({
  canRestore,
  onRestore,
  resourceType,
}) => {
  const { i18n } = useLocalization();
  const [showDialog, setShowDialog] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setRestoring(true);
    setError(null);
    try {
      const result = await onRestore();
      if (result) setShowDialog(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <HStack className="bg-warning-100 rounded-xl p-3 px-4 items-center gap-3 mt-4 mb-8">
        <Archive className="text-warning-600" size={16} />
        <Text className="text-sm text-warning-700 flex-1">
          {resourceType === "study"
            ? i18n.t("misc.archivedStudyBanner")
            : i18n.t("misc.archivedQuestionBanner")}
        </Text>
        {canRestore && (
          <Button
            className="px-0 py-0 bg-transparent"
            onPress={() => setShowDialog(true)}
            size="sm"
            variant="link"
          >
            <ButtonText className="text-sm text-warning-700 font-bold no-underline">
              {i18n.t("misc.restore")}
            </ButtonText>
          </Button>
        )}
      </HStack>

      <DeleteDialog
        body={
          resourceType === "study"
            ? i18n.t("studies.confirmRestoreMessage")
            : i18n.t("adhoc.confirmRestoreMessage")
        }
        deleteButtonText={i18n.t("misc.restore")}
        error={error}
        isOpen={showDialog}
        loading={restoring}
        loadingText={i18n.t("misc.restoring")}
        onCancel={() => setShowDialog(false)}
        onDelete={handleConfirm}
        title={
          resourceType === "study"
            ? i18n.t("studies.restoreStudy")
            : i18n.t("adhoc.restoreQuestion")
        }
      />
    </>
  );
};

export default ArchivedBanner;
